/**
 * AWS S3 bucket types and environments for PiBo application.
 */
export type PiBoBucket =
  | 'content-public'
  | 'content-public-staging'
  | 'content-private'
  | 'content-private-staging'
  | 'data-dumping'
  | 'data-dumping-staging'
  | 'royalty'
  | 'royalty-staging'
  | (string & {})

/**
 * AWS S3 bucket types available in the PiBo application.
 * Each bucket type has production and staging variants.
 */
export type BucketType = 'content-public' | 'content-private' | 'data-dumping' | 'royalty'

/**
 * Application environment types.
 */
export type Environment = 'production' | 'staging'

/**
 * Maps bucket types and environments to their corresponding AWS S3 bucket names.
 */
const BUCKET_MAP: Record<BucketType, Record<Environment, PiBoBucket>> = {
  'content-public': {
    production: 'content-public',
    staging: 'content-public-staging',
  },
  'content-private': {
    production: 'content-private',
    staging: 'content-private-staging',
  },
  'data-dumping': {
    production: 'data-dumping',
    staging: 'data-dumping-staging',
  },
  royalty: {
    production: 'royalty',
    staging: 'royalty-staging',
  },
}

/**
 * Retrieves the appropriate AWS S3 bucket name based on the bucket type and environment.
 *
 * @param bucketType - The type of bucket (content-public, content-private, or data-dumping)
 * @param environment - The deployment environment (production or staging)
 * @returns The fully qualified S3 bucket name
 *
 * @remarks
 * This function ensures type-safe bucket name resolution across different environments.
 * All staging buckets follow the naming convention: `{bucket-name}-staging`
 *
 * @example
 * // Get production static asset bucket
 * const bucket = getS3BucketName('content-public', 'production')
 * console.log(bucket) // 'content-public'
 *
 * @example
 * // Get staging books bucket
 * const stagingBucket = getS3BucketName('content-private', 'staging')
 * console.log(stagingBucket) // 'content-private-staging'
 *
 * @example
 * // Using with environment variable
 * const env = process.env.NODE_ENV === 'production' ? 'production' : 'staging'
 * const dataBucket = getS3BucketName('data-dumping', env)
 */
export function getS3BucketName(bucketType: BucketType, environment: Environment): PiBoBucket {
  return BUCKET_MAP[bucketType][environment]
}

/**
 * Retrieves all available bucket names for a specific environment.
 *
 * @param environment - The deployment environment (production or staging)
 * @returns Array of all S3 bucket names for the specified environment
 *
 * @example
 * // Get all production buckets
 * const prodBuckets = getAllBucketsForEnvironment('production')
 * console.log(prodBuckets)
 * // ['content-public', 'content-private', 'data-dumping']
 *
 * @example
 * // Get all staging buckets
 * const stagingBuckets = getAllBucketsForEnvironment('staging')
 * console.log(stagingBuckets)
 * // ['content-public-staging', 'content-private-staging', 'data-dumping-staging']
 */
export function getAllBucketsForEnvironment(environment: Environment): PiBoBucket[] {
  return Object.values(BUCKET_MAP).map((buckets) => buckets[environment])
}

/**
 * Retrieves both production and staging bucket names for a specific bucket type.
 *
 * @param bucketType - The type of bucket to retrieve
 * @returns Object containing both production and staging bucket names
 *
 * @example
 * const contentPublicBuckets = getBucketsByType('content-public')
 * console.log(contentPublicBuckets)
 * // { production: 'content-public', staging: 'content-public-staging' }
 */
export function getBucketsByType(bucketType: BucketType): Record<Environment, PiBoBucket> {
  return BUCKET_MAP[bucketType]
}
